import React, {
	memo,
	useState,
	useCallback,
	useContext,
	useRef,
	useMemo,
	useEffect,
	useLayoutEffect,
} from 'react';
import { css } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl';

import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation/types';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import type { DocNode } from '@atlaskit/adf-schema/doc';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type MutationUpdaterInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { NewReplyContainer } from '@confluence/inline-comments-common/entry-points/styled.ts';
import type { CommentInlineLocation } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import {
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
// Keep the runtime enum separate because some consumers fully mock commentDataManagerUtils.
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { handleAgentMentionInComment } from '@confluence/agent-mention/entry-points/handleAgentMentionInComment.ts';
import { useAgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/useAgentMentionFyiNudge.ts';
import { AgentMentionFyiNudgeAsync as AgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/AgentMentionFyiNudgeAsync.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useCreateObjectCommentMutation } from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import {
	type CreateInlineReplyInteropOptions,
	useCreateInlineReplyMutation,
} from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutation.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useContentType } from '@confluence/page-context';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import {
	REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import {
	getValidDate,
	formatTimestampForAriaLabel,
} from '@confluence/comments-util/entry-points/dateUtils.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useAgentSessionsContext } from '@confluence/agent-mention/entry-points/AgentSessionsContext.ts';
import { useOptimisticCommentAgentSessions } from '@confluence/agent-mention/entry-points/useOptimisticCommentAgentSessions.ts';
import {
	reconcileOptimisticAgentReplies,
	commitOptimisticAgentReplyMatches,
	markAgentRepliesRendered,
} from '@confluence/agent-mention/entry-points/reconcileOptimisticAgentReplies.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { RepliesToggler } from '@confluence/comments-common/entry-points/RepliesToggler.ts';
import { RepliesFooter } from '@confluence/comments-common/entry-points/RepliesFooter.ts';
import { useReplyFocusRestoration } from '@confluence/comments-common/entry-points/useReplyFocusRestoration.ts';
import {
	ViewReplyOptions,
	getCommentReplies,
} from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import { useVisibleReplies } from '@confluence/comments-common/entry-points/useVisibleReplies.ts';
import { GeneralReplyScroller } from '@confluence/comments-common/entry-points/GeneralReplyScroller.ts';
import type { RenderPrivateReply } from '@confluence/comments-common/entry-points/useCommentAgentPrivateReply.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { useAnnotationsIsOffline } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';

import {
	onSaveReply,
	getReplyEditorContent,
	getDefaultNumVisibleReplies,
	getDefaultReplyView,
	type SaveProps,
} from '../helper/commentThreadHelper.tsx';
import { arePropsShallowEqual } from '../helper/arePropsShallowEqual.ts';

import { Comment } from './Comment.tsx';

const commentThreadRepliesBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingLeft: token('space.150'),
	paddingRight: token('space.100'),
});

const commentThreadRepliesBoxStandaloneStyleOverrides = css({
	paddingRight: token('space.150'),
});

const privateReplyTargetStyles = css({
	marginInlineStart: `calc(-1 * ${token('space.150')})`,
	marginInlineEnd: `calc(-1 * ${token('space.100')})`,
});

const commentHandlesPaddingReplyBoxOverrides = css({
	paddingTop: token('space.0'),
	paddingLeft: token('space.0'),
	paddingBottom: token('space.0'),
});

const replyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});

export type CommentRepliesProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	editCommentQueryId?: string;
	selectedAnnotation: AnnotationInfo;
	parentCommentId: string;
	canAddComments: boolean;
	isStandaloneComment?: boolean;
	deleteStandaloneComment?: (parentCommentId: string, commentId: string) => void;
	replyToComment?: (commentId: string, commentData: CommentData | ReplyData) => void;
	updateReactionsCacheFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterInterop<any> | void;
	onRendered?: () => void;
	eventEmitter?: AnnotationUpdateEmitter;
	isEditorExpanded?: boolean;
	hasParentMaxHeight?: boolean;
	flags?: FlagsStateContainer;
	hideReactions?: boolean;
	/** True only when rendered inside the comment summary popup (via CommentThread). */
	isCommentSummaryPopup?: boolean;
	isMediaViewerComment?: boolean;
	renderPrivateReply?: RenderPrivateReply;
	privateReplyTarget?: React.ReactNode;
};

const i18n = defineMessages({
	replyAriaLabel: {
		id: 'comments-panel.reply.aria-label',
		defaultMessage: 'Reply by {authorName}, {timestamp}',
		description: 'Aria label for reply container announcing the author and timestamp',
	},
});

const CommentRepliesComponent = ({
	parentComment,
	pageInfo,
	pageMode,
	editCommentQueryId,
	selectedAnnotation,
	parentCommentId,
	canAddComments,
	isStandaloneComment,
	deleteStandaloneComment,
	replyToComment,
	updateReactionsCacheFn,
	onRendered,
	eventEmitter,
	isEditorExpanded = false,
	hasParentMaxHeight = false,
	flags,
	hideReactions = false,
	isCommentSummaryPopup = false,
	isMediaViewerComment = false,
	renderPrivateReply,
	privateReplyTarget,
}: CommentRepliesProps): React.JSX.Element => {
	const { isReactionsEnabled } = useContext(ReactionsContext);
	// A reply renders its agent-session status row below itself; read the page's agent-session
	// map so we can keep the connector line visible for such a reply (mirrors the footer's
	// ObjectCommentNestableReplies), otherwise the status floats when the reply is last.
	const agentSessions = useAgentSessionsContext();
	// A waiting private reply moves after the replies, so keep the line through the last reply.
	const hasPrivateReplyAtThreadEnd =
		!!renderPrivateReply &&
		[parentComment.id, ...parentComment.replies.flatMap((reply) => (reply ? [reply.id] : []))].some(
			(id) => agentSessions.get(id)?.state === 'INPUT_REQUIRED',
		) &&
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true);

	const pageId = pageInfo?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	const { createInlineReplyInterop } = useCreateInlineReplyMutation({ contentId: pageId });

	const { addCommentInCache } = useAllCommentsData({
		contentId: pageId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const createObjectCommentMutationFn = useCreateObjectCommentMutation();

	const intl = useIntl();
	const { formatMessage } = intl;

	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { cloudId, userId, displayName, activationId } = useSessionData();
	const {
		showFyi,
		agent,
		onContentChange: onAgentMentionContentChange,
	} = useAgentMentionFyiNudge({
		cloudId,
		enabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
	});

	// Track whether the FYI component has ever been shown, so we only load the
	// chunk on first mention but keep it mounted for the collapse animation.
	const hasShownFyiRef = useRef(false);
	useEffect(() => {
		if (showFyi) {
			hasShownFyiRef.current = true;
		}
	}, [showFyi]);

	const handleContentChangeWithData = useCallback(
		(_hasChanges: boolean, adf?: DocNode) => {
			onAgentMentionContentChange(adf);
		},
		[onAgentMentionContentChange],
	);

	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const replyAnalyticsSource = isMediaViewerComment
		? AnalyticsSource.MEDIA_VIEWER_SIDEBAR
		: isStandaloneComment
			? AnalyticsSource.STANDALONE
			: AnalyticsSource.COMMENTS_PANEL;
	const [
		{ replyToCommentId, replyToCommentUser, replyToContributorUsers },
		{ setReplyToCommentId },
	] = useCommentsPanel();
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const isOffline = useAnnotationsIsOffline();
	const { commentService } = useCommentService();
	const [contentType] = useContentType();

	const threadReplies = useMemo(
		() =>
			parentComment.replies.filter(
				(r): r is ReplyData =>
					r !== null &&
					(parentComment.location as CommentInlineLocation).inlineMarkerRef ===
						(r.location as CommentInlineLocation).inlineMarkerRef,
			),
		[parentComment.replies, parentComment.location],
	);
	// Cache cleanup propagates through an effect in the background loader. Reconcile
	// before deriving the visible list so the intermediate snapshot never renders two rows.
	const allReplies = useMemo(
		() =>
			expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
				? reconcileOptimisticAgentReplies(threadReplies)
				: threadReplies,
		[threadReplies],
	);

	const numOfReplies = allReplies.length;

	const unreadReplyIds = useMemo(() => {
		return allReplies.filter((r) => unreadCommentIDSet.has(r.id)).map((r) => r.id);
	}, [allReplies, unreadCommentIDSet]);

	const [, setCommentForEdit] = useState('');

	const { showModal } = useDialogs();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const commentType = parentComment.type;
	const inlineCommentLocation = parentComment?.location as CommentInlineLocation;
	const isDanglingComment =
		commentType === CommentType.INLINE &&
		inlineCommentLocation?.inlineResolveProperties?.isDangling &&
		!isStandaloneComment;
	const isParentCommentOpen = parentComment.isOpen;
	// Analytics only; unlike `isDanglingComment` this is not narrowed for the standalone view.
	const isParentCommentDangling = Boolean(
		inlineCommentLocation?.inlineResolveProperties?.isDangling,
	);

	const isParentCommentResolvedOrDangling = !isParentCommentOpen || Boolean(isDanglingComment);

	const [currentReplyView, setReplyView] = useState<ViewReplyOptions>(
		// Determine the reply view by counting the total number of top-level replies to the parent comment.
		// This excludes any nested replies.
		getDefaultReplyView(
			getCommentReplies(allReplies, parentCommentId).length,
			isParentCommentResolvedOrDangling,
			isStandaloneComment,
			isMediaViewerComment,
		),
	);

	// User page permissions
	const operations = pageInfo?.operations || [];

	// User space permissions
	const spaceOperations = pageInfo?.space?.operations || [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	const hasMediaUploadPermissions = canAddComment && canAddAttachment;

	const threadKey =
		commentType === CommentType.INLINE
			? (parentComment?.location as CommentInlineLocation).inlineMarkerRef || ''
			: parentCommentId;

	// When in summarizer popup, force empty actions on replies.
	// reopen won't be rendered for reply, but we are using it to render disabled reaction summary for replies
	//
	// Keep this array stable so unchanged reply rows can skip parent-driven renders.
	const supportedTopLevelActions = useMemo<CommentAction[]>(() => {
		if (isCommentSummaryPopup) {
			return [];
		}
		if (isParentCommentOpen) {
			return ['edit', 'resolve', 'delete'];
		}
		return ['reopen'];
	}, [isCommentSummaryPopup, isParentCommentOpen]);

	// When inside the summarizer popup, never expand the reply editor inline.
	let expandReplyEditor = false;
	if (isCommentSummaryPopup) {
		expandReplyEditor = false;
	} else {
		expandReplyEditor = isEditorExpanded;
	}

	// Track if we've already fired the analytics event for current visibleReplies state
	const hasFiredForVisibleRepliesRef = useRef<string>('');

	const topLevelReplyToNestedChildrenMap = useMemo(() => {
		const topLevelReplyIds = new Set(
			allReplies.filter((reply) => reply.parentId === parentCommentId).map((reply) => reply.id),
		);

		// We need to get all replies that are nested to any depth under a top level reply.
		// This will help us render the GeneralReplyScroller component for each reply (mainly, we need to show user icons and the blue dot indicator)
		const topLevelReplyToNestedChildrenMapInternal: {
			[key: string]: ReplyData[];
		} = Array.from(topLevelReplyIds).reduce<{
			[key: string]: ReplyData[];
		}>((acc, replyId) => {
			acc[replyId] = [];
			return acc;
		}, {});
		const replyIdToReplyMap = allReplies.reduce<{
			[key: string]: ReplyData;
		}>((acc, reply) => {
			acc[reply.id] = reply;
			return acc;
		}, {});

		// Map all replies to their top level parent. Replies can be nested to any depth, so we need to traverse the tree.
		const filteredReplies = allReplies.filter((reply) => !topLevelReplyIds.has(reply.id));

		for (const filteredReply of filteredReplies) {
			let replyParentId = filteredReply.parentId;

			// Add a circuit breaker to prevent infinite loops if we get really wonky data
			let circuitBreaker = 0;
			while (replyParentId && circuitBreaker < 100 && !topLevelReplyIds.has(replyParentId)) {
				replyParentId = replyIdToReplyMap[replyParentId]?.parentId;
				circuitBreaker++;
			}

			if (replyParentId && replyParentId in topLevelReplyToNestedChildrenMapInternal) {
				// Second condition will always be true, unless we somehow hit circuit breaker above
				topLevelReplyToNestedChildrenMapInternal[replyParentId].push(filteredReply);
			}
		}

		return topLevelReplyToNestedChildrenMapInternal;
	}, [allReplies, parentCommentId]);

	// Determine the latest top level replies to show on the thread's initial load
	const latestReplies = useMemo(() => {
		const replies: ReplyData[] = [];
		const topLevelReplies = allReplies.filter(
			(reply) => reply.id in topLevelReplyToNestedChildrenMap,
		);

		// Sort by createdAtNonLocalized value
		topLevelReplies.sort((a, b) => {
			// Safely handle missing or invalid dates
			const dateA = getValidDate(a?.createdAtNonLocalized);
			const dateB = getValidDate(b?.createdAtNonLocalized);

			return dateA.getTime() - dateB.getTime();
		});

		// Add the latest replies to the replies array
		for (
			let i = 0;
			i <
			getDefaultNumVisibleReplies(
				numOfReplies,
				isStandaloneComment,
				isParentCommentResolvedOrDangling,
			);
			i++
		) {
			if (topLevelReplies.length > 0) {
				const reply = topLevelReplies.pop();
				if (reply) {
					// Unshift to maintain reply chronological order
					replies.unshift(reply);
				}
			}
		}

		return replies;
	}, [
		allReplies,
		isStandaloneComment,
		numOfReplies,
		topLevelReplyToNestedChildrenMap,
		isParentCommentResolvedOrDangling,
	]);

	/*
	 * Determine the latest top level reply IDs and their nested reply IDs.
	 * This is passed to RepliesToggler to determine the hidden replies.
	 */
	const latestReplyIds = useMemo(() => {
		const latestAndNestedReplyIds: Set<string> = new Set();

		for (const reply of latestReplies) {
			latestAndNestedReplyIds.add(reply.id);

			for (const nestedReply of topLevelReplyToNestedChildrenMap[reply.id]) {
				latestAndNestedReplyIds.add(nestedReply.id);
			}
		}

		return latestAndNestedReplyIds;
	}, [latestReplies, topLevelReplyToNestedChildrenMap]);

	// Render-time derivation of visible replies. Computing during render (rather
	// than syncing state in a layout effect) lets visible replies appear in SSR
	// HTML and removes a post-mount re-render on the client. Agent replies also
	// consume the reconciled snapshot in this render.
	const visibleReplies = useVisibleReplies({
		allReplies,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		parentCommentId,
	});
	useLayoutEffect(() => {
		if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
			commitOptimisticAgentReplyMatches(threadReplies);
			markAgentRepliesRendered(visibleReplies.map((reply) => reply.id));
		}
	}, [threadReplies, visibleReplies]);
	const optimisticSessions = useOptimisticCommentAgentSessions(
		visibleReplies.map((reply) => reply.id),
	);
	const {
		getReplyContainer,
		requestFocusOnRevealedReply,
		requestFocusOnShowReplies,
		setReplyContainerRef,
		showRepliesButtonRef,
	} = useReplyFocusRestoration(visibleReplies);

	// Track replies rendered state in experience tracker
	// Extract visibleReplies length to avoid complex expression in dependency array
	const visibleRepliesLength = visibleReplies.length;

	useEffect(() => {
		const repliesRendered = currentReplyView === ViewReplyOptions.ALL && visibleReplies.length > 0;

		// Fire GASV3 event only when in ALL view with replies and we haven't fired for this state yet
		if (currentReplyView === ViewReplyOptions.ALL && visibleReplies.length > 0) {
			const stateKey = `${currentReplyView}-${visibleReplies.length}`;

			// Only fire if we haven't already fired for this exact state
			if (hasFiredForVisibleRepliesRef.current !== stateKey && numOfReplies !== 0) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'repliesRenderedStateChanged',
						actionSubject: 'comment',
						actionSubjectId: parentCommentId,
						objectType: pageType,
						objectId: pageId,
						source: isStandaloneComment
							? AnalyticsSource.STANDALONE
							: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							commentType: commentType === CommentType.GENERAL ? 'page' : 'inline',
							repliesRendered,
							currentReplyView,
							numOfReplies,
							visibleRepliesCount: visibleReplies.length,
						},
					},
				}).fire();

				// Mark this state as fired
				hasFiredForVisibleRepliesRef.current = stateKey;
			}
		}
	}, [
		currentReplyView,
		visibleReplies,
		visibleRepliesLength,
		numOfReplies,
		commentType,
		experienceTracker,
		createAnalyticsEvent,
		parentCommentId,
		pageType,
		pageId,
		isStandaloneComment,
	]);

	const removeAnnotationHighlight = useCallback(() => {
		if (pageMode === PageMode.VIEW) {
			eventEmitter?.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
		} else {
			eventEmitter?.emit('setselectedannotation', null);
		}
	}, [pageMode, eventEmitter]);

	// Track whether this CommentReplies is inside the comment summary popup.
	const isInsidePopupRef = useRef(isCommentSummaryPopup);
	isInsidePopupRef.current = isCommentSummaryPopup;

	// Reset ref on component unmount
	useEffect(() => {
		return () => {
			hasFiredForVisibleRepliesRef.current = '';

			// When inside the popup, skip resetting the panel's reply state on unmount.
			if (isInsidePopupRef.current) {
				return;
			}

			//TODO: Remove once https://product-fabric.atlassian.net/browse/COMMENTS-5857 is resolved
			const isDatabaseOrWhiteboard = contentType === 'database' || isCanvasContentType(contentType);
			if (
				!isDatabaseOrWhiteboard &&
				expValEquals('confluence_comment_reset_reply_options', 'isEnabled', true)
			) {
				setReplyToCommentId();
				removeAnnotationHighlight();
			}
		};
	}, [removeAnnotationHighlight, setReplyToCommentId, contentType]);

	const handleReplyButtonClick = () => {
		// When inside the summarizer popup, delegate to the popup's reply handler
		// so it can explicitly close the popup and then open the reply editor in the panel.
		// Check for unsaved draft first so the user can choose to keep it.
		if (isCommentSummaryPopup && replyToComment) {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						replyToComment(parentCommentId, parentComment);
					},
				});
			} else {
				replyToComment(parentCommentId, parentComment);
			}
			return;
		}

		// Skip if there is no active inline comment annotation and currentlySelectedComment is not general
		const shouldRemoveAnnotationHighlight =
			commentType === CommentType.GENERAL && selectedAnnotation.id;

		if (hasContentChanged) {
			// if the content has changed and the user has clicked reply button to another comment thread, warn the user
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setReplyToCommentId({ parentCommentId, location: LocationValue.PANEL });
					shouldRemoveAnnotationHighlight && eventEmitter && removeAnnotationHighlight();
				},
			});
		} else {
			setReplyToCommentId({ parentCommentId, location: LocationValue.PANEL });
			shouldRemoveAnnotationHighlight && eventEmitter && removeAnnotationHighlight();
		}
	};

	// Helper function to check if the reply editor is visible in the comments panel
	const isReplyEditorVisible = () => {
		const currentDocument = typeof document !== 'undefined' ? document : undefined;

		if (!currentDocument) {
			return false;
		}
		// Use combined selector to find the specific reply editor for this comment thread
		const replyEditorContainer = currentDocument.querySelector(
			`[data-testid="comment-thread-${parentCommentId}-replies"] [data-testid="comments-panel-new-reply-container"]`,
		);
		const panelContainer = currentDocument.querySelector(
			'[data-testid="comments-panel-list-container"]',
		);

		if (!replyEditorContainer || !panelContainer) {
			return false;
		}

		const replyEditorRect = replyEditorContainer.getBoundingClientRect();
		const panelRect = panelContainer.getBoundingClientRect();

		// Check if the bottom of the reply editor container is visible within the panel
		return replyEditorRect.bottom <= panelRect.bottom;
	};

	const editorChangeHandler = () => {
		let experienceName = REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE;

		if (commentType === CommentType.GENERAL) {
			experienceName = REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE;
		}

		experienceTracker.succeed({
			name: experienceName,
		});

		// Check if we need to scroll to show the reply editor
		// Only scroll if the reply editor is for this specific comment thread
		if (
			!isMediaViewerComment &&
			replyToCommentId === parentCommentId &&
			!isReplyEditorVisible() &&
			expValEquals('confluence_comment_bring_replyeditor_intoview', 'isEnabled', true)
		) {
			// Use combined selector to find the specific reply editor for this comment thread
			const replyEditorContainer =
				typeof document !== 'undefined'
					? document.querySelector(
							`[data-testid="comment-thread-${parentCommentId}-replies"] [data-testid="comments-panel-new-reply-container"]`,
						)
					: null;
			if (replyEditorContainer) {
				replyEditorContainer.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}
		}
	};

	// Create the inline reply via the Comments Data Manager interop.
	const replyCreationInteropWrapper = useCallback(
		(options: CreateInlineReplyInteropOptions, onCacheUpdateError: (error: Error) => void) =>
			createInlineReplyInterop({
				options,
				onCacheUpdateError,
			}),
		[createInlineReplyInterop],
	);

	// We need to intercept the raw mutation result to extract the new comment ID for agent mention
	// tagging. We can't chain .then() on onSaveReply() directly because onSaveReply's internal
	// .then() consumes the mutation data and returns undefined. Instead, we wrap the mutation fn
	// before passing it in, so we can tap the result before onSaveReply processes it.
	const buildAgentMentionCreateInlineReplyFn = (adf: any): SaveProps['createInlineReplyFn'] =>
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? (...args: Parameters<NonNullable<SaveProps['createInlineReplyFn']>>) => {
					const promise = (replyCreationInteropWrapper as SaveProps['createInlineReplyFn'])!(
						...args,
					);
					void promise?.then(({ data }: any) => {
						const commentId = data?.replyInlineComment?.id ?? parentCommentId;
						handleAgentMentionInComment(
							adf,
							cloudId,
							pageId,
							commentId,
							userId ?? undefined,
							'inline',
							createAnalyticsEvent,
							contentType
								? {
										contentType,
										pageContext:
											pageMode === PageMode.VIEW ? 'confluence-page-view' : 'confluence-page-edit',
										activationId,
									}
								: undefined,
						);
					});
					return promise;
				}
			: (replyCreationInteropWrapper as SaveProps['createInlineReplyFn']);

	const renderCommentEditor = () => {
		const editor = (
			<CommentEditor
				pageId={pageId}
				pageType={pageType}
				content={getReplyEditorContent({ replyToCommentUser, replyToContributorUsers })}
				appearance="chromeless"
				EditorComponent={ChromelessEditor}
				onSaveComment={(adf, onSuccess) => {
					return onSaveReply({
						pageId,
						parentCommentId,
						cloudId,
						adf,
						onSuccess,
						pageMode,
						editCommentQueryId,
						setCommentForEdit,
						createAnalyticsEvent,
						pageType,
						experienceTracker,
						createInlineReplyFn: buildAgentMentionCreateInlineReplyFn(adf),
						createObjectReplyFn: createObjectCommentMutationFn,
						addCommentInCommentsDataManagerCacheFn: addCommentInCache,
						setReplyToCommentId,
						resetContentChanged,
						setReplyView,
						selectedAnnotation,
						threadKey,
						commentType,
						isDangling: isParentCommentDangling,
						searchSessionId,
						isReactionsEnabled,
						analyticsSource: replyAnalyticsSource,
						contentTypesCommentService: commentService,
						userId,
						displayName,
					});
				}}
				onContentChange={onChange}
				onContentChangeWithData={handleContentChangeWithData}
				onEditorReady={editorChangeHandler}
				commentMode="reply"
				commentType={commentType === CommentType.GENERAL ? 'page' : 'inline'}
				spaceId=""
				showCancelButton
				useNewWarningModal
				hideWatchCheckbox
				expandEditor={expandReplyEditor}
				pageMode="view"
				hasMediaUploadPermissions={hasMediaUploadPermissions}
				topLevelCommentId={parentComment.id}
				shouldWarnOnInternalNavigation
				onCancelComment={({
					destroy = false,
				}: {
					destroy?: boolean;
				} = {}) => {
					// when the transition happens the
					// other reply list item is clicked
					// and we need to reset ssetReplyToCommentId
					if (!destroy || replyToCommentId !== parentCommentId) {
						setReplyToCommentId();
					}
				}}
				isCommentsPanel
				isMediaViewerComment={isMediaViewerComment}
				isOffline={isOffline}
			/>
		);

		const editorWithFyi = (
			<>
				{(showFyi || hasShownFyiRef.current) &&
					expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) && (
						<AgentMentionFyiNudge visible={showFyi} agent={agent} />
					)}
				{editor}
			</>
		);

		return (
			<NewReplyContainer
				mode="view"
				isCommentsPanel
				isFabricPage
				data-testid="comments-panel-new-reply-container"
			>
				{editorWithFyi}
			</NewReplyContainer>
		);
	};

	// Make reply containers tabbable for accessibility
	useEffect(() => {
		if (visibleReplies.length > 0) {
			visibleReplies.forEach((reply) => {
				const replyContainer = getReplyContainer(reply.id);
				if (replyContainer) {
					replyContainer.setAttribute('tabIndex', '0');
				}
			});
		}
	}, [getReplyContainer, visibleReplies]);

	const renderRepliesToggler = () => (
		<RepliesToggler
			replies={allReplies}
			unreadReplyIds={unreadReplyIds}
			setReplyView={setReplyView}
			currentReplyView={currentReplyView}
			commentType={commentType}
			isParentOpen={isParentCommentOpen}
			canAddComments={canAddComments}
			latestReplyIds={latestReplyIds}
			isStandaloneComment={isStandaloneComment}
			analyticsSource={replyAnalyticsSource}
			expandReplyEditor={expandReplyEditor}
			showRepliesButtonRef={showRepliesButtonRef}
			onShowHiddenReplies={requestFocusOnRevealedReply}
		/>
	);

	return (
		<>
			{numOfReplies > 0 && !isMediaViewerComment && renderRepliesToggler()}
			<div
				data-testid={`comment-thread-${parentComment.id}-replies`}
				css={[
					commentThreadRepliesBoxStyles,
					isStandaloneComment && commentThreadRepliesBoxStandaloneStyleOverrides,
				]}
			>
				<div>
					{visibleReplies.length > 0 && (
						<div data-testid={`comment-thread-${parentComment.id}-reply-list`}>
							{visibleReplies.map((reply, idx) => {
								const isLastUnnestableReply = idx === visibleReplies.length - 1;

								// Keep the connector line visible when this reply has an agent-session status
								// row below it. Gate inlined, ordered last per feature-gating rules.
								const replyHasAgentSession =
									(Boolean(agentSessions.get(reply.id)) || optimisticSessions.has(reply.id)) &&
									expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true);

								return (
									<div
										ref={(replyContainer) => setReplyContainerRef(reply.id, replyContainer)}
										css={[replyBoxStyles, commentHandlesPaddingReplyBoxOverrides]}
										key={`comment-thread-${parentComment.id}-replies-${idx}`}
										data-testid={`comment-thread-${parentComment.id}-replies-${reply.id}`}
										aria-label={formatMessage(i18n.replyAriaLabel, {
											authorName:
												reply.author?.displayName || formatMessage(commentsi18n.anonymous),
											timestamp: formatTimestampForAriaLabel({
												intl,
												dateString: reply.createdAtNonLocalized,
												isReply: true,
											}),
										})}
									>
										<Comment
											flags={flags}
											comment={reply as ReplyData}
											supportedTopLevelActions={supportedTopLevelActions}
											hideReactions={hideReactions}
											pageInfo={pageInfo}
											pageMode={pageMode}
											isUnread={unreadCommentIDSet.has(reply.id)}
											threadKey={threadKey}
											// When the parent comment is resolved, hide the styles for the last reply —
											// but keep them when the reply has an agent-session row so the line reaches it.
											hideBranchingStyle={
												!replyHasAgentSession &&
												!hasPrivateReplyAtThreadEnd &&
												((!canAddComments && isLastUnnestableReply) ||
													(isStandaloneComment && isLastUnnestableReply) ||
													(isLastUnnestableReply && !expandReplyEditor))
											}
											adjustForNestedReplies={
												commentType === CommentType.GENERAL &&
												reply.id in topLevelReplyToNestedChildrenMap &&
												topLevelReplyToNestedChildrenMap[reply.id]?.length > 0
											}
											canAddComments={canAddComments}
											isParentCommentOpen={isParentCommentOpen}
											isParentCommentDangling={isParentCommentDangling}
											deleteStandaloneComment={deleteStandaloneComment}
											isStandaloneComment={isStandaloneComment}
											isMediaViewerComment={isMediaViewerComment}
											replyToComment={replyToComment}
											updateReactionsCacheFn={updateReactionsCacheFn}
											onRendered={onRendered}
											isLastUnnestableReply={isLastUnnestableReply}
											isReplyEditorOpen={expandReplyEditor}
											renderPrivateReply={renderPrivateReply}
											commentHandlesPadding
											hasParentMaxHeight={hasParentMaxHeight}
										/>
										{commentType === CommentType.GENERAL &&
											reply.id in topLevelReplyToNestedChildrenMap &&
											topLevelReplyToNestedChildrenMap[reply.id]?.length > 0 && (
												<GeneralReplyScroller
													nestedReplies={topLevelReplyToNestedChildrenMap[reply.id]}
													unreadReplyIds={unreadReplyIds}
													targetCommentIdForScroll={reply.id}
													isLastUnnestableReply={isLastUnnestableReply}
												/>
											)}
									</div>
								);
							})}
						</div>
					)}
					{privateReplyTarget && <div css={privateReplyTargetStyles}>{privateReplyTarget}</div>}
				</div>
				{/* The Media Viewer sidebar has no reply button, so it only shows the open reply editor. */}
				{(!isMediaViewerComment || expandReplyEditor) && (
					<RepliesFooter
						replyView={currentReplyView}
						setReplyView={setReplyView}
						isEditorExpanded={!isCommentSummaryPopup && isEditorExpanded}
						isParentCommentOpen={isParentCommentOpen}
						isStandaloneComment={isStandaloneComment}
						requestFocusOnShowReplies={requestFocusOnShowReplies}
						handleReplyButtonClick={handleReplyButtonClick}
						numOfVisibleReplies={visibleReplies.length}
						isOffline={isOffline}
						scrollToId={threadKey}
						location={LocationValue.PANEL}
					>
						{renderCommentEditor()}
					</RepliesFooter>
				)}
			</div>
		</>
	);
};

// Shallow-equal props skip re-rendering the replies subtree during unrelated parent renders.
export const CommentReplies: typeof CommentRepliesComponent = memo(
	CommentRepliesComponent,
	arePropsShallowEqual,
) as typeof CommentRepliesComponent;
